import { capitalizeIdentifier } from "@gadgetinc/connection";
import path from "path";
import { JavaScriptReservedIdentifiers } from "../../../../EditValidations";
import { importDefaultFromFileStatement } from "../../../../action-code-templates/importStatementCodeGenerators";
import { nextNewObjectName } from "../../../../nameGenerator";
import { ReservedGadgetNames } from "../../../../naming/ReservedGadgetNames";
const illegalCodeEffectImportNames = new Set([...ReservedGadgetNames, ...JavaScriptReservedIdentifiers]);

/**
 * Returns the filename without the filetype extension after validation
 * Example:
 * `modelApiId/create/onCreateSuccess-A.js` -> `onCreateSuccess_A`
 */
function getCodeEffectImportNameFromFilePath(filePath: string) {
  return toValidIdentifier(path.parse(filePath).name) ?? "userImport";
}

export function getImportFilePathToImportNameRecord(importFilePaths: string[]): Record<string, string> {
  const pathToImportName: Record<string, string> = {};
  const importNames = new Set<string>();

  for (const path of importFilePaths) {
    let importName = getCodeEffectImportNameFromFilePath(path);

    if (illegalCodeEffectImportNames.has(importName) || importNames.has(importName)) {
      // For collisions and reserved keywords
      importName = nextNewObjectName(importNames, importName);
    }
    importNames.add(importName);
    pathToImportName[path] = importName;
  }
  return removeFileExtensions(pathToImportName);
}

function removeFileExtensions(pathToImportName: Record<string, string>) {
  const newPathToImportName: Record<string, string> = {};
  function removeFileExtensionIfPossible(extension: string) {
    for (const path in pathToImportName) {
      if (path.endsWith(extension)) {
        const noExtensionPath = path.slice(0, -extension.length);

        if (!newPathToImportName[noExtensionPath]) {
          // Not in the record already. Add it without the extension and remove the original
          newPathToImportName[noExtensionPath] = pathToImportName[path];
          delete pathToImportName[path];
        }
      }
    }
  }

  // Only these file extensions are valid if omitted
  removeFileExtensionIfPossible(".js"); // Highest priority
  removeFileExtensionIfPossible(".ts");
  removeFileExtensionIfPossible(".cts"); // Lowest priority

  return { ...pathToImportName, ...newPathToImportName };
}

export function buildAllUserFileImportStatementCode(importPathsToImportNames: Record<string, string>) {
  const importStatements = [];
  for (const path of Object.keys(importPathsToImportNames)) {
    importStatements.push(importDefaultFromFileStatement(importPathsToImportNames[path], path));
  }
  return importStatements.join("\n");
}

/**
 * @param str Takes a string and attempts to convert it to a valid js identifier
 * It will also perform a camelize on the string
 * It will also check if the string is a reserved keyword and if so, it will append an underscore to the end
 * If the resulting string is empty it will return undefined
 */
export const toValidIdentifier = (str: string) => {
  let identifier = str.replace(/-/g, "_");

  identifier = identifier.replace(/[^a-zA-Z0-9_]/g, "");

  identifier = identifier.trim();
  identifier = capitalizeIdentifier(identifier, false);

  // if the string leads with a number, prepend an underscore
  if (identifier.length > 0 && identifier[0] && !isNaN(parseInt(identifier[0]))) {
    identifier = "_" + identifier;
  }

  return identifier !== "" ? identifier : undefined;
};

export function getImportNameFromRawFilePathWithExtension(rawCodeEffectPath: string, codeEffectPathsToImportNames: Record<string, string>) {
  return (
    codeEffectPathsToImportNames[rawCodeEffectPath] ??
    codeEffectPathsToImportNames[rawCodeEffectPath.replaceAll(".js", "").replaceAll(".ts", "").replaceAll(".cts", "")]
  );
}
